<template>
  <div class="bbp-step-form">
    <BBPForm
      @close="closeForm"
      @save="saveForm"
      @update="checkDates"
      :disabledButton="disabledButton"
      :btnTextRight="project.id ? 'SAVE' : 'ADD'"
      :title="titleForm"
      :default-model="updateBody"
      :updateBody="updateBody"
      :inputs="inputs"
      :project="project"
    />
    <transition name="bbp-fade" appear>
      <BBPLoader v-if="loaderRun"></BBPLoader>
    </transition>
  </div>
</template>

<script>
import BBPForm from '@/components/Main/BBPForm/BBPForm'
import stepsInputs from '@/constants/InputsSettings/stepsInputs'
import moment from 'moment'
import {cloneDeep, compact, findIndex, get, orderBy, set, filter} from 'lodash'
import {HOURLY, REGULAR, SIDE_HOURLY} from '@/constants/projectTypes'
import { LOCALHOST_DOMAIN_ID } from '@/constants/domainListPlatform'
import UserApi from '@/api/Users/api'
import StepsApi from '@/api/Steps/api'
import FilesApi from '@/api/Files/api'
import TimerApi from '@/api/Timer/api'
import ProjectsApi from '@/api/Projects/api'
import BBPLoader from '@/components/Main/BBPLoader/BBPLoader'
import setFileBody from '@/mixin/setFileBody'
import getWithElasticsearch from '@/mixin/getWithElasticsearch'
import {IN_PROGRESS_PROJECT} from '@/constants/projectStatuses'
import setProjectBody from '@/mixin/setProjectBody'
import convertToLocationDate from '@/mixin/convertToLocationDate'
import removeUserNameOnComment from '@/mixin/removeUserNameOnComment'

export default {
  name: 'ProjectUpdateForm',
  props: ['project', 'clientProjects', 'defaultBody', 'hideSubProject', 'isListOFProject'],
  components: {BBPLoader, BBPForm},
  mixins: [
    setFileBody,
    getWithElasticsearch,
    setProjectBody,
    convertToLocationDate,
    removeUserNameOnComment
  ],
  data () {
    return {
      updateBody: {
        project: this.project,
        subproject: false,
        dateStart: new Date(),
        dateNextUpdate: new Date(moment(new Date()).add(3, 'days').toString())
      },
      titleForm: 'Update project',
      loaderRun: false,
      stepsInputs: stepsInputs,
      projectId: '',
      subProjects: []
    }
  },
  computed: {
    disabledButton () {
      const { project = {} } = this.updateBody
      const { projectType = {} } = project
      return this.subProjects.length === 0 && projectType.id === HOURLY.id
    },
    inputs () {
      const typeId = get(this.currentProjectType, 'id', this.currentProjectType)
      return typeId === REGULAR.id ? this.regularInputs : this.hourlyInputs
    },
    currentProjectType () {
      const typeFromProject = get(this.project, 'projectType', {})
      return get(this.updateBody, 'project.projectType', typeFromProject)
    },
    projectsInProgress () {
      const projectsInProgress = this.clientProjects.filter((el) => {
        const { status, projectType = {} } = el
        const { id } = projectType
        return status === IN_PROGRESS_PROJECT.id && SIDE_HOURLY.id !== id
      })
      return projectsInProgress
    },
    regularInputs () {
      const filtered = this.stepsInputs.filter(item => (!item.projectType || item.projectType === REGULAR.id) &&
        item.isHide !== true)
      return filtered.map(input => {
        if (input.type === 'select' && input.model === 'project') {
          input.options = this.projectsInProgress
        }
        return input
      })
    },
    hourlyInputs () {
      const filtered = this.stepsInputs.filter(item => (!item.projectType || item.projectType === HOURLY.id) &&
        (this.hideSubProject === false ? item : item.isHide !== true))
      return filtered.map(input => {
        const subProjectsInProgress = filter(this.subProjects, {status: IN_PROGRESS_PROJECT.id})
        if (input.type === 'select' && input.model === 'project') {
          input.options = this.projectsInProgress
        } else if (input.type === 'select' && input.model === 'subproject') {
          input.options = subProjectsInProgress
          input.disabled = this.subProjects.length === 0
        }
        return input
      })
    },
    isRegularProject () {
      return get(this.currentProjectType, 'id', '') === REGULAR.id
    },
    isSubProject () {
      const { projectType = {} } = this.project
      return projectType.id === SIDE_HOURLY.id
    },
    userInfo () {
      return this.$store.getters.userInfo
    },
    usersListProjects: {
      get () {
        return this.$store.getters.usersListProjects
      },
      set (data) {
        this.$store.dispatch('setUsersListProjects', data)
      }
    },
    stepsList: {
      get () {
        return this.$store.getters.stepsList
      },
      set (data) {
        this.$store.dispatch('setStepsList', data)
      }
    },
    projectsList: {
      get () {
        return this.$store.getters.projectsList
      },
      set (data) {
        this.$store.dispatch('setProjectsList', data)
      }
    },
    currentDomain () {
      return this.$store.getters.currentDomain
    }
  },
  methods: {
    setDefaultBody () {
      if (this.isSubProject && this.isListOFProject) {
        const { parentProject = {} } = this.project
        this.updateBody = {
          ...this.updateBody,
          project: parentProject,
          subproject: this.project
        }
      }
      if (!this.defaultBody._id) return
      const { totalTime = 0 } = this.defaultBody
      this.updateBody = cloneDeep(this.defaultBody)
      this.updateBody.project = this.project
      const user = get(this.defaultBody, 'user', '')
      const { commentsClientHistory, companies } = this.updateBody
      this.updateBody.user = user[0]
      this.updateBody.commentText = this.updateBody.comment
      this.updateBody.companies = companies.length ? companies : [{}]
      this.updateBody.clientIsTab = !!commentsClientHistory.length
      this.updateBody.companyIsTab = !!companies.length
      this.updateBody.totalTime = totalTime
    },
    closeForm () {
      this.$emit('close')
    },
    async updateSubTimer (totalTime, { _id }) {
      try {
        await TimerApi.updateSubTimer({totalTime}, _id)
      } catch (err) {
        console.error(err)
      }
    },
    updateLastStep (data) {
      if (this.isSubProject) {
        const { parentProjectId } = this.project
        const parentProject = this.projectsList.find((el) => el._id === parentProjectId)
        if (parentProject) this.$set(parentProject, 'lastStep', data)
        this.$set(this.project, 'lastStep', data)
      } else if (this.isRegularProject) {
        this.$set(this.project, 'lastStep', data)
      } else {
        const { project } = data
        const [, hoursProject] = project
        const childrenProject = this.projectsList.find((el) => el._id === hoursProject)
        if (childrenProject) this.$set(childrenProject, 'lastStep', data)
        this.$set(this.project, 'lastStep', data)
      }
    },
    createStep (step) {
      StepsApi.createStep(step)
        .then(({ data }) => {
          const { project = [] } = data
          this.$emit('addStep')
          this.updateLastStep(data)
          const projectId = project[project.length - 1]
          const stepListProject = get(this.stepsList, projectId, [])
          if (stepListProject.length) {
            stepListProject.push(data)
            const sortedStepsByDate = orderBy(stepListProject, ['dateStart'], ['desc'])
            set(this.stepsList, projectId, sortedStepsByDate)
          } else {
            set(this.stepsList, projectId, [data])
          }
          this.stepsList = cloneDeep(this.stepsList)
          this.updateDatesProject(data, stepListProject)
          if (!this.isRegularProject) this.$emit('updateGenerateInvoice', project)
        })
        .catch(err => {
          this.loaderRun = false
          console.error(err)
        })
    },
    updateStep (step) {
      StepsApi.updateStep(step)
        .then(({ data }) => {
          const stepId = get(data, '_id', '')
          const { project } = data
          const projectId = project[project.length - 1]
          const stepListProject = get(this.stepsList, projectId, [])
          const index = findIndex(stepListProject, {_id: stepId})
          if (index !== -1) {
            stepListProject.splice(index, 1, data)
          }
          const sortedStepsByDate = orderBy(stepListProject, ['dateStart'], ['desc'])
          set(this.stepsList, projectId, sortedStepsByDate)
          this.stepsList = cloneDeep(this.stepsList)
          this.updateDatesProject(data, stepListProject)
          if (!this.isRegularProject) this.$emit('updateGenerateInvoice', project)
        })
        .catch(err => {
          this.loaderRun = false
          console.error(err)
        })
    },
    updateDatesProject ({subTimers, user, dateNextUpdate, dateStart}, stepListProject = []) {
      this.project.dateLastUpdate = new Date()
      this.project.dateNextUpdate = dateNextUpdate
      const project = this.setProjectBody(this.project)
      project.commentStartDate = stepListProject.length ? undefined : dateStart
      ProjectsApi.editProject(project)
        .then(({ data }) => {
          const { totalTime = 0, commentStartDate } = data
          this.project.commentStartDate = commentStartDate
          this.project.totalTime = totalTime
          this.closeForm()
          this.loaderRun = false
          if (subTimers) {
            const { _id } = subTimers
            const { subTimers: subTimersArray = [] } = this.project
            subTimers.user = user
            const currentIndex = subTimersArray.findIndex((item) => item._id === _id)
            if (currentIndex !== -1) {
              this.project.subTimers.splice(currentIndex, 1, subTimers)
            } else {
              this.project.subTimers.unshift(subTimers)
            }
          }
        })
        .catch(err => {
          this.loaderRun = false
          console.error(err)
        })
    },
    async saveForm (val) {
      this.loaderRun = true
      const { _id, subTimers = {} } = this.defaultBody
      const { startDateEndDate = {} } = val
      if (_id) {
        const { totalTime } = startDateEndDate
        if (totalTime) {
          await this.updateSubTimer(totalTime, subTimers)
        }
      }
      const step = this.generateBodyStep(val)
      step.id ? this.updateStep(step) : this.createStep(step)
    },
    cDateBackEnd (value, startDate) {
      const time = moment(value).format('HH:mm:ss')
      const date = moment(startDate).format('YYYY-MM-DD')
      const fullDate = `${date}T${time}`
      return this.convertToLocationDate(fullDate)
    },
    generateProjectId (project, subproject) {
      const projectId = project.id || project._id
      const subprojectId = subproject.id || subproject._id
      const { parentProjectId } = this.project
      if (this.isRegularProject) {
        return [projectId]
      } else if (this.isSubProject) {
        return [parentProjectId, this.project._id || this.project.id]
      } else if (subprojectId) {
        return [projectId, subprojectId]
      } else if (parentProjectId) {
        return parentProjectId ? [parentProjectId, projectId] : [projectId]
      }
    },
    createDate ({endDate, startDate, totalTime}, dateStart) {
      const { _id } = this.defaultBody
      if (_id) {
        return {
          totalTime
        }
      } else {
        return {
          endDate: endDate ? this.cDateBackEnd(endDate, dateStart) : this.cDateBackEnd(new Date(), dateStart),
          startDate: startDate ? this.cDateBackEnd(startDate, dateStart) : this.cDateBackEnd(new Date(), dateStart)
        }
      }
    },
    generateBodyStep (data) {
      const {
        commentText,
        clientIsTab,
        companyIsTab,
        startDateEndDate = {},
        subproject = {},
        project = {}
      } = data
      const { companiesSelect = [] } = this.project
      const commentUsers = get(this.project, 'commentUsers', [])
      const files = get(data, 'uploadFile', [])
      const dateStart = get(data, 'dateStart', this.dateStart)
      const result = {
        project: this.generateProjectId(project, subproject),
        dateStart,
        dateNextUpdate: get(data, 'dateNextUpdate', this.dateNextUpdate),
        actionsTaken: get(data, 'actionsTaken', ''),
        nextSteps: get(data, 'nextSteps', ''),
        uploadFile: compact(files.map(file => file.id)),
        comment: {
          text: this.removeUserNameOnComment(commentText, commentUsers),
          users: commentUsers.length ? commentUsers.map((el) => el._id) : []
        },
        clientIsTab,
        companyIsTab,
        companies: companyIsTab ? companiesSelect : [],
        domain: this.currentDomain ? this.currentDomain.id : LOCALHOST_DOMAIN_ID,
        ...this.createDate(startDateEndDate, dateStart)
      }
      const stepId = get(this.defaultBody, '_id', '')
      if (stepId) result.id = stepId
      const spendTime = get(data, 'spendTime', '')
      if (spendTime) result.spendTime = spendTime
      const selectedUserId = get(data, 'user._id', '')
      const currentUserId = get(this.userInfo, 'id', '')
      if (currentUserId || selectedUserId) result.user = selectedUserId || currentUserId
      return result
    },
    async uploadFile (file) {
      const body = this.setFileBody(file)
      try {
        const resp = await FilesApi.uploadFile(body)
        return resp.data
      } catch (e) {
        console.error(e)
        return ''
      }
    },
    checkDates ({model, input, value}) {
      const { id, _id, projectType = {}, parentProjectId } = value
      const ids = id || _id
      if (input.model === 'dateStart') {
        const addThreeDays = moment(value).add(3, 'days').toString()
        this.$set(model, 'dateNextUpdate', new Date(addThreeDays))
        this.updateBody = {...model}
        this.projectId = ids
      } else if (input.model === 'project') {
        const isSub = projectType.id === SIDE_HOURLY.id
        this.projectId = isSub ? parentProjectId : ids
        this.updateProject(projectType, isSub)
        this.updateBody = model
      }
    },
    updateProject (projectType) {
      if (projectType.id === REGULAR.id) return
      this.getSubProjects()
    },
    getSubProjects () {
      ProjectsApi.getProjects({
        parentProjectId: this.projectId,
        projectType: SIDE_HOURLY.id,
        status: IN_PROGRESS_PROJECT.id
      })
        .then(({ data }) => {
          this.disabled = this.subProjects.length === 0
          this.subProjects = data.projects
          const model = {...this.updateBody}
          if (this.project.projectType.id === 2) {
            model.subproject = this.subProjects[0]
            this.updateBody = model
          }
        })
        .catch((err) => {
          this.loaderRun = false
          console.error(err)
        })
    },
    async getUsersListSelect () {
      if (this.usersListProjects.length) {
        this.setUsersInputs()
        return
      }
      this.$emit('loader', true)
      try {
        const { data } = await UserApi.getUsersListSelect({})
        this.$emit('loader', false)
        this.usersListProjects = data
        this.setUsersInputs()
      } catch (err) {
        console.error(err)
      }
    },
    setUsersInputs () {
      const users = this.stepsInputs.find((el) => el.model === 'user')
      const commentText = this.stepsInputs.find((el) => el.model === 'commentText')
      users.options = this.usersListProjects
      commentText.options = this.usersListProjects
    }
  },
  beforeMount () {
    this.setDefaultBody()
    this.getUsersListSelect()
  }
}
</script>

<style lang="scss" scoped>
@import "../../../../sass/variables";
@import "../../../../sass/mixins";

.bbp-step-form {
  @include global-font;
  max-height: 90vh;
  width: 520px;
}

</style>



// WEBPACK FOOTER //
// src/components/Projects/ProjectCard/ProjectUpdateForm/ProjectUpdateForm.vue